<template>
  <div class="ui-input w-100">
    <v-card
      flat
      elevation="0"
      :class="['k-tiptap-input', { 'k-tiptap-input--error': error }]"
      color="#f0f0f0"
    >
      <editor-content v-if="editor" :editor="editor" />
      <v-btn
        icon
        variant="text"
        color="var(--secondary)"
        size="x-small"
        class="btn-icon px-1"
        @click="showMentionMenu"
      >
        <ui-icon name="ellipsis-vertical" />
      </v-btn>
    </v-card>
    <div
      v-if="error || (Array.isArray(errorMessages) && errorMessages.length)"
      class="text-error ui-hint mt-1 ml-1"
    >
      <div v-for="(msg, i) in normalizedMessages" :key="i">{{ msg }}</div>
    </div>
  </div>
</template>

<script>
  import { Editor, EditorContent } from "@tiptap/vue-3";
  import StarterKit from "@tiptap/starter-kit";
  import Mention from "@tiptap/extension-mention";
  import tippy from "tippy.js";
  import { VueRenderer } from "@tiptap/vue-3";
  import MentionList from "@/components/MentionList.vue";

  export default {
    components: {
      EditorContent,
    },
    props: {
      modelValue: String,
      fields: {
        type: Object,
        default: () => {},
      },
      format: {
        type: String,
        default: "text",
      },
      error: {
        type: Boolean,
        default: false,
      },
      errorMessages: {
        type: [String, Array],
        default: () => [],
      },
    },
    data() {
      return {
        editor: null,
        mentionSuggestion: null, // We will store the mention suggestion here
        lastContent: "", // Track the last content to prevent unnecessary updates
      };
    },
    computed: {
      normalizedMessages() {
        if (typeof this.errorMessages === "string") {
          return [this.errorMessages];
        }
        return Array.isArray(this.errorMessages) ? this.errorMessages : [];
      },
    },

    mounted() {
      this.initializeEditor();
    },
    beforeUnmount() {
      if (this.editor) {
        this.editor.destroy();
      }
    },
    watch: {
      // Watch for changes in the modelValue to set content in the editor
      modelValue(newValue) {
        // Avoid setting content if it's the same as the current content
        if (this.editor && this.editor.getHTML() !== newValue) {
          this.editor.commands.setContent(newValue);
        }
      },
    },
    methods: {
      initializeEditor() {
        this.editor = new Editor({
          extensions: [
            StarterKit,
            Mention.configure({
              HTMLAttributes: {
                class: "mention",
                "data-user": null,
              },
              suggestion: this.createSuggestion(),
              renderHTML({ options, node }) {
                return node.attrs.label || node.attrs.id;
              },
            }),
          ],
          content: this.modelValue || "",
          editorProps: {
            attributes: {
              "data-format": this.format, // of een class
            },
          },
          onUpdate: ({ editor }) => {
            let currentContent = editor.getHTML();

            // Zorg ervoor dat de inhoud alleen geüpdatet wordt als het daadwerkelijk verandert
            if (this.lastContent !== currentContent) {
              this.lastContent = currentContent;
              this.$emit("update:modelValue", currentContent);
            }
          },

          onKeyDown: ({ event }) => {
            // Dit voorkomt dat een nieuwe <p> wordt toegevoegd als de gebruiker op Enter drukt in een lege paragraaf
            if (event.key === "Enter") {
              const selection = this.editor.state.selection;
              const node = this.editor.view.nodeDOM(selection.$from.pos);

              if (node && node.nodeName === "P" && node.textContent.trim() === "") {
                event.preventDefault(); // Voorkomt het invoegen van een lege <p>
              }
            }
          },
        });
      },

      // Method to open the mention menu manually
      showMentionMenu() {
        if (!this.editor) return;

        this.editor.commands.focus();
        this.editor.commands.insertContent("@");
      },

      createSuggestion() {
        return {
          items: ({ query }) => {
            return (this.fields || [])
              .filter((col) =>
                col.column_name.toLowerCase().includes(query.toLowerCase())
              )
              .slice(0, 15)
              .map((col) => ({
                id: col.id,
                label: col.column_name,
                title: col.label,
                field_type: col.field_type,
                "data-user": col["data-user"] ?? null,
              }));
          },
          render: () => {
            let component;
            let popup;

            return {
              onStart: (props) => {
                if (!props.clientRect) return;

                component = new VueRenderer(MentionList, {
                  props: {
                    ...props,
                    editor: this.editor,
                  },
                  editor: this.editor,
                });
                popup = tippy(document.body, {
                  getReferenceClientRect: props.clientRect,
                  appendTo: () => document.body,
                  content: component.element,
                  showOnCreate: true,
                  interactive: true,
                  trigger: "manual",
                  placement: "bottom-start",
                });
              },
              onUpdate: (props) => {
                if (!props.clientRect) return;

                if (component) {
                  component.updateProps(props);
                }

                if (popup) {
                  popup.setProps({
                    getReferenceClientRect: props.clientRect,
                  });
                }
              },
              onKeyDown: (props) => {
                if (props.event.key === "Escape" && popup) {
                  popup.hide();
                  return true;
                }

                return component && component.ref?.onKeyDown(props);
              },
              onExit: () => {
                if (popup) {
                  popup.destroy();
                  popup = null;
                }

                if (component) {
                  component.destroy();
                  component = null;
                }
              },
            };
          },
        };
      },
    },
  };
</script>

<style>
  .k-tiptap-input {
    border-radius: 8px;
    padding: 7.5px;
    width: 100%;
    word-break: break-all;

    p {
      font-size: 12px;
    }

    .tiptap {
      outline: none !important;
      width: calc(100% - 30px);
    }

    .v-btn {
      position: absolute;
      right: 2px;
      top: 2px;
      border-radius: 4px;
      max-height: 26px;
    }
  }

  .mention {
    background-color: var(--primary-50);
    border-radius: 0.4rem;
    color: var(--tertiary);
    padding: 0.3rem 0.5rem;
    border: 1px solid var(--primary-100);
    margin-right: 2px;
    word-break: keep-all;
  }

  .k-tiptap-input--error {
    border: 1px solid var(--ui-red) !important;
    background-color: var(--ui-red-50) !important;
  }
</style>
